import {AlertTriangleIcon, CheckCircle2Icon, ChevronDownIcon, FileTextIcon, TrendingDownIcon, TrendingUpIcon, XCircleIcon} from "lucide-react";
import {useMemo, useState} from "react";

import {buildComboChart, buildWaterfallChart, SEMANTIC_COLORS} from "@components/charts/echarts-toolkit";
import {EChartsCard} from "@components/charts/EChartsCard";
import {Button} from "@components/ui/button";
import {Card, CardContent} from "@components/ui/card";
import {Skeleton} from "@components/ui/skeleton";
import {
    type ThreeStatementCheck,
    useVNextBusinessContext,
    useVNextCashFlowFromExplorer,
    useVNext3StatementForecast,
    type VNextMonth,
} from "@hooks/use-vnext";

import {ExecutiveSummary} from "./ExecutiveSummary";

interface CashFlowSummaryTabProps {
    workspaceId: string;
    scenarioId: string;
    actualMonths?: number;
    forecastMonths?: number;
    period?: string;
    selectedMonth?: string;
    customFrom?: string;
    customTo?: string;
    compare?: string;
    fyStartMonth?: number;
    currentMonth?: string;
}

interface MonthlySnapshot {
    monthKey: string;
    label: string;
    status: "actual" | "forecast";
    opening: number;
    inflow: number;
    outflow: number;
    net: number;
    closing: number;
}

interface ScenarioRow {
    label: string;
    monthlyNet: number;
    runwayMonths: number;
    assessment: "healthy" | "watch" | "critical";
}

function formatCurrency(value: number): string {
    const formatted = `$${Math.abs(value).toLocaleString(undefined, {maximumFractionDigits: 0})}`;
    return value < 0 ? `(${formatted})` : formatted;
}

function formatCompact(value: number): string {
    const abs = Math.abs(value);
    if (abs >= 1_000_000) {
        const s = `$${(abs / 1_000_000).toFixed(1)}M`;
        return value < 0 ? `(${s})` : s;
    }
    if (abs >= 1_000) {
        const s = `$${(abs / 1_000).toFixed(0)}K`;
        return value < 0 ? `(${s})` : s;
    }
    return formatCurrency(value);
}

function formatMonths(months: number): string {
    if (!Number.isFinite(months)) return "\u221e";
    return `${months.toFixed(1)} mo`;
}

function monthLabel(month: VNextMonth): string {
    const [yearStr, monthStr] = month.month_key.split("-");
    const year = Number(yearStr);
    const m = Number(monthStr);
    if (!year || !m) return month.label || month.month_key;
    return new Date(year, m - 1, 1).toLocaleString("en-US", {month: "short", year: "2-digit"});
}

function nodeValue(rows: VNextMonth["rows"], nodeId: string): number {
    return rows.find((row) => row.node_id === nodeId)?.value ?? 0;
}

function assessmentFromRunway(runwayMonths: number): ScenarioRow["assessment"] {
    if (!Number.isFinite(runwayMonths) || runwayMonths >= 6) return "healthy";
    if (runwayMonths >= 3) return "watch";
    return "critical";
}

function scenarioRow(label: string, monthlyNet: number, currentCash: number): ScenarioRow {
    const runwayMonths = monthlyNet >= 0 ? Number.POSITIVE_INFINITY : currentCash / Math.abs(monthlyNet);
    return {
        label,
        monthlyNet,
        runwayMonths,
        assessment: assessmentFromRunway(runwayMonths),
    };
}

function openTab(tab: string) {
    if (typeof window === "undefined") return;
    window.dispatchEvent(new CustomEvent("vnext-ui-action", {detail: {tab}}));
}

function cfForecastToSnapshot(month: VNextMonth): MonthlySnapshot {
    const v = (id: string) => month.rows.find((r) => r.node_id === id)?.value ?? 0;
    const ops = v("__cf_net_operations__");
    const inv = v("__cf_net_investing__");
    const fin = v("__cf_net_financing__");
    return {
        monthKey: month.month_key,
        label: monthLabel(month),
        status: "forecast",
        opening: v("__cf_opening_cash__"),
        inflow: Math.max(0, ops) + Math.max(0, inv) + Math.max(0, fin),
        outflow: Math.abs(Math.min(0, ops)) + Math.abs(Math.min(0, inv)) + Math.abs(Math.min(0, fin)),
        net: v("__cf_net_movement__"),
        closing: v("__cf_closing_cash__"),
    };
}

export function CashFlowSummaryTab({
    workspaceId,
    scenarioId,
    actualMonths,
    forecastMonths = 0,
    period,
    selectedMonth,
    customFrom,
    customTo,
    fyStartMonth,
    currentMonth,
}: CashFlowSummaryTabProps) {
    const {months, isLoading: explorerLoading, error: explorerError} = useVNextCashFlowFromExplorer(
        workspaceId,
        {
            scenarioId,
            forecastMonths,
            actualMonths,
            period,
            selectedMonth,
            customFrom,
            customTo,
            fyStartMonth,
            currentMonth,
        },
    );
    const {data: forecastData, isLoading: forecastLoading} = useVNext3StatementForecast(
        workspaceId, scenarioId, forecastMonths, actualMonths,
    );
    const {contextItems} = useVNextBusinessContext(workspaceId, "approved");

    const isLoading = explorerLoading || forecastLoading;
    const error = explorerError;

    const snapshots = useMemo<MonthlySnapshot[]>(() => {
        const actualSnaps: MonthlySnapshot[] = months
            .filter((m) => m.status === "actual")
            .map((month) => {
                const inflow = month.rows
                    .filter((row) => (row.category_key || "").split(".")[0] === "inflow")
                    .reduce((sum, row) => sum + row.value, 0);
                const rawOutflow = month.rows
                    .filter((row) => (row.category_key || "").split(".")[0] === "outflow")
                    .reduce((sum, row) => sum + row.value, 0);

                return {
                    monthKey: month.month_key,
                    label: monthLabel(month),
                    status: month.status,
                    opening: nodeValue(month.rows, "__opening_cash__"),
                    inflow,
                    outflow: Math.abs(rawOutflow),
                    net: nodeValue(month.rows, "__net_movement__"),
                    closing: nodeValue(month.rows, "__closing_cash__"),
                };
            });

        const forecastSnaps: MonthlySnapshot[] = forecastMonths > 0
            ? (forecastData?.cf?.months ?? [])
                .filter((m) => m.status === "forecast")
                .map(cfForecastToSnapshot)
            : [];

        const actualKeys = new Set(actualSnaps.map((s) => s.monthKey));
        const merged = [
            ...actualSnaps,
            ...forecastSnaps.filter((s) => !actualKeys.has(s.monthKey)),
        ];
        merged.sort((a, b) => a.monthKey.localeCompare(b.monthKey));
        return merged;
    }, [months, forecastData?.cf?.months, forecastMonths]);

    const hasForecast = snapshots.some((s) => s.status === "forecast");

    const basis = useMemo(() => {
        const actual = snapshots.filter((row) => row.status === "actual");
        return actual.length > 0 ? actual : snapshots;
    }, [snapshots]);

    // currentCash = closing balance of the last snapshot shown in the table
    // (actual or forecast), so the Key Metrics card always matches the table's
    // last row. When the last snapshot is a forecast, the label reflects that.
    const lastSnap = snapshots.at(-1);
    const currentCash = lastSnap?.closing ?? 0;
    const currentCashAsOf = lastSnap?.label;
    const currentCashIsForecast = lastSnap?.status === "forecast";
    const firstOpening = snapshots[0]?.opening ?? 0;
    const cashDelta = currentCash - firstOpening;

    const avgInflow = basis.length ? basis.reduce((sum, row) => sum + row.inflow, 0) / basis.length : 0;
    const avgOutflow = basis.length ? basis.reduce((sum, row) => sum + row.outflow, 0) / basis.length : 0;
    const avgNet = basis.length ? basis.reduce((sum, row) => sum + row.net, 0) / basis.length : 0;
    const worstNet = basis.length ? Math.min(...basis.map((row) => row.net)) : 0;
    const stressRunway = worstNet < 0 ? currentCash / Math.abs(worstNet) : Number.POSITIVE_INFINITY;
    const opexCoverage = avgOutflow > 0 ? currentCash / avgOutflow : Number.POSITIVE_INFINITY;
    const positiveMonths = basis.filter((row) => row.net >= 0).length;

    const scenarios = useMemo<ScenarioRow[]>(() => {
        if (!snapshots.length) return [];
        return [
            scenarioRow("Current trend", avgNet, currentCash),
            scenarioRow("Revenue -10%", avgInflow * 0.9 - avgOutflow, currentCash),
            scenarioRow("Revenue -25%", avgInflow * 0.75 - avgOutflow, currentCash),
            scenarioRow("Worst month repeats", worstNet, currentCash),
            scenarioRow("Revenue -50%", avgInflow * 0.5 - avgOutflow, currentCash),
            scenarioRow("Receipts stop", 0 - avgOutflow, currentCash),
        ];
    }, [avgInflow, avgOutflow, avgNet, currentCash, snapshots.length, worstNet]);

    const firstForecast = snapshots.find((row) => row.status === "forecast")?.label;

    const monthlyFlowOption = useMemo(
        () =>
            buildComboChart({
                categories: snapshots.map((row) => row.label),
                barSeries: [
                    {
                        key: "receipts",
                        label: "Receipts",
                        data: snapshots.map((row) => row.inflow),
                        color: SEMANTIC_COLORS.primary,
                    },
                    {
                        key: "payments",
                        label: "Payments",
                        data: snapshots.map((row) => -row.outflow),
                        color: "#CBD5E1",
                    },
                ],
                lineSeries: [
                    {
                        key: "closing_cash",
                        label: "Net Cash Position",
                        data: snapshots.map((row) => row.closing),
                        color: SEMANTIC_COLORS.accent,
                        yAxisIndex: 1,
                    },
                ],
                yFormat: "compact_currency",
                y2Format: "compact_currency",
                forecastStart: firstForecast,
            }),
        [firstForecast, snapshots],
    );

    const bridgeOption = useMemo(
        () =>
            buildWaterfallChart({
                categories: ["Opening", ...snapshots.map((row) => row.label), "Closing"],
                values: [firstOpening, ...snapshots.map((row) => row.net), currentCash],
                totalIndices: [0, snapshots.length + 1],
                yFormat: "compact_currency",
                colors: {
                    positive: SEMANTIC_COLORS.positive,
                    negative: SEMANTIC_COLORS.negative,
                    total: SEMANTIC_COLORS.primaryDark,
                },
            }),
        [currentCash, firstOpening, snapshots],
    );

    const generatedInsights = useMemo(() => {
        const notes: {type: "insight" | "warning"; text: string}[] = [];

        if (avgNet >= 0) {
            notes.push({type: "insight", text: `Average monthly net cash is positive at ${formatCompact(avgNet)}.`});
        } else {
            notes.push({type: "warning", text: `Average monthly net cash is negative at ${formatCompact(avgNet)}.`});
        }

        if (Number.isFinite(stressRunway) && stressRunway < 3) {
            notes.push({type: "warning", text: `Stress runway is short at ${formatMonths(stressRunway)} if worst month repeats.`});
        } else if (Number.isFinite(stressRunway)) {
            notes.push({type: "insight", text: `Stress runway is ${formatMonths(stressRunway)} using worst historical month.`});
        }

        if (cashDelta >= 0) {
            notes.push({type: "insight", text: `Cash increased ${formatCompact(cashDelta)} across the selected period.`});
        } else {
            notes.push({type: "warning", text: `Cash declined ${formatCompact(cashDelta)} across the selected period.`});
        }

        return notes;
    }, [avgNet, cashDelta, stressRunway]);

    if (isLoading) {
        return (
            <div className="space-y-4 p-4">
                <Skeleton className="h-24 w-full rounded-xl" />
                <Skeleton className="h-48 w-full rounded-xl" />
                <Skeleton className="h-64 w-full rounded-xl" />
            </div>
        );
    }

    if (error) {
        return (
            <Card>
                <CardContent className="p-6 text-center text-red-600">{error}</CardContent>
            </Card>
        );
    }

    if (!snapshots.length) {
        return (
            <Card>
                <CardContent className="p-10 text-center text-muted-foreground">
                    No cash flow summary data available yet.
                </CardContent>
            </Card>
        );
    }

    const topContext = contextItems.slice(0, 4);

    return (
        <div className="flex-1 min-h-0 min-w-0 overflow-y-auto bg-[#F8FAFC] p-4 pb-8">
            <div className="mx-auto w-full max-w-[1400px] space-y-6" data-tour="dashboard-cf-tab">
                <header className="rounded-2xl bg-gradient-to-br from-[#209DB3] to-[#1A8C9E] p-6 shadow-sm">
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                        <div>
                            <p className="text-[11px] font-semibold uppercase tracking-[2px] text-[#CCFFF2]">
                                Cash Flow | CEO Review Summary
                            </p>
                            <h2 className="mt-2 text-2xl font-extrabold text-white">Cash Position &amp; Runway Snapshot</h2>
                            <p className="mt-1 text-sm text-white/85">
                                {hasForecast
                                    ? "Actuals from Xero Bank Summary, net of credit-card/overdraft balances \u00B7 Forecast from 3-statement engine"
                                    : "Actuals from Xero Bank Summary snapshots, net of credit-card/overdraft balances"}
                            </p>
                            <p className="mt-2 text-xs text-white/80">
                                Summary is read-only. Use Forecast Summary for full statement detail and forecast edits.
                            </p>
                        </div>
                        <Button
                            type="button"
                            variant="secondary"
                            className="shrink-0 bg-white text-[#0F172A] hover:bg-white/90"
                            onClick={() => openTab("forecast_summary")}
                        >
                            Open Forecast Summary
                        </Button>
                    </div>
                </header>

                <ExecutiveSummary workspaceId={workspaceId} scenarioId={scenarioId} tabKey="cashflow_summary" period={period} selectedMonth={selectedMonth} customFrom={customFrom} customTo={customTo} fyStartMonth={fyStartMonth} currentMonth={currentMonth} />

                <section data-tour="dashboard-cf-metrics">
                    <h3 className="mb-3 text-sm font-bold uppercase tracking-[2px] text-[#209DB3]">Key Metrics</h3>
                    <div className="grid gap-4 md:grid-cols-3 xl:grid-cols-6">
                        <MetricCard
                            label={currentCashAsOf
                                ? `${currentCashIsForecast ? "Projected" : "Current"} Net Cash (${currentCashAsOf})`
                                : "Current Net Cash Position"}
                            value={formatCompact(currentCash)}
                        />
                        <MetricCard label="Avg Monthly Receipts" value={formatCompact(avgInflow)} />
                        <MetricCard label="Avg Monthly Payments" value={formatCompact(avgOutflow)} />
                        <MetricCard label="Avg Monthly Net Flow" value={formatCompact(avgNet)} emphasis={avgNet >= 0 ? "good" : "bad"} />
                        <MetricCard label="Stress Runway" value={formatMonths(stressRunway)} emphasis={Number.isFinite(stressRunway) && stressRunway < 3 ? "bad" : "neutral"} />
                        <MetricCard label="OpEx Coverage" value={formatMonths(opexCoverage)} emphasis={Number.isFinite(opexCoverage) && opexCoverage < 3 ? "bad" : "good"} />
                    </div>
                    <p className="mt-2 text-xs text-muted-foreground">
                        Positive months: {positiveMonths}/{basis.length}
                    </p>
                </section>

                <div className="grid gap-6 xl:grid-cols-2">
                    <section className="rounded-2xl border border-[#E2E8F0] bg-white p-5 shadow-sm">
                        <h3 className="mb-3 text-sm font-bold uppercase tracking-[2px] text-[#209DB3]">Monthly Cash Flows</h3>
                        <EChartsCard option={monthlyFlowOption} height={300} />
                    </section>
                    <section className="rounded-2xl border border-[#E2E8F0] bg-white p-5 shadow-sm">
                        <h3 className="mb-3 text-sm font-bold uppercase tracking-[2px] text-[#209DB3]">Cash Bridge</h3>
                        <EChartsCard option={bridgeOption} height={300} />
                    </section>
                </div>

                <section className="rounded-2xl border border-[#E2E8F0] bg-white p-5 shadow-sm">
                    <h3 className="mb-3 text-sm font-bold uppercase tracking-[2px] text-[#209DB3]">Monthly Snapshot</h3>
                    <div className="overflow-x-auto border rounded-lg">
                        <table className="w-full text-xs">
                            <thead>
                                <tr className="bg-[#0F172A] text-white">
                                    <th className="px-3 py-2 text-left">Month</th>
                                    <th className="px-3 py-2 text-right">Opening</th>
                                    <th className="px-3 py-2 text-right">Receipts</th>
                                    <th className="px-3 py-2 text-right">Payments</th>
                                    <th className="px-3 py-2 text-right">Net Change</th>
                                    <th className="px-3 py-2 text-right">Net Cash</th>
                                </tr>
                            </thead>
                            <tbody>
                                {snapshots.map((row) => (
                                    <tr key={row.monthKey} className={`border-b border-gray-100 hover:bg-gray-50/50 ${row.status === "forecast" ? "bg-amber-50/30" : ""}`}>
                                        <td className="px-3 py-2 font-medium">
                                            {row.label}
                                            <span className={`ml-2 text-[10px] ${row.status === "forecast" ? "font-semibold text-amber-600" : "text-muted-foreground"}`}>
                                                ({row.status === "forecast" ? "F" : "actual"})
                                            </span>
                                        </td>
                                        <td className="px-3 py-2 text-right tabular-nums">{formatCurrency(row.opening)}</td>
                                        <td className="px-3 py-2 text-right tabular-nums">{formatCurrency(row.inflow)}</td>
                                        <td className="px-3 py-2 text-right tabular-nums text-red-600">{formatCurrency(row.outflow)}</td>
                                        <td className={`px-3 py-2 text-right tabular-nums font-semibold ${row.net >= 0 ? "text-green-600" : "text-red-600"}`}>
                                            {formatCurrency(row.net)}
                                        </td>
                                        <td className="px-3 py-2 text-right tabular-nums font-semibold">{formatCurrency(row.closing)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </section>

                <div className="grid gap-6 xl:grid-cols-2">
                    <section className="rounded-2xl border border-[#E2E8F0] bg-white p-5 shadow-sm" data-tour="dashboard-cf-runway">
                        <h3 className="mb-3 text-sm font-bold uppercase tracking-[2px] text-[#209DB3]">Runway Scenarios</h3>
                        <div className="overflow-x-auto border rounded-lg">
                            <table className="w-full text-xs">
                                <thead>
                                    <tr className="bg-[#0F172A] text-white">
                                        <th className="px-3 py-2 text-left">Scenario</th>
                                        <th className="px-3 py-2 text-right">Monthly Net</th>
                                        <th className="px-3 py-2 text-right">Runway</th>
                                        <th className="px-3 py-2 text-left">Assessment</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {scenarios.map((row) => (
                                        <tr key={row.label} className="border-b border-gray-100">
                                            <td className="px-3 py-2 font-medium">{row.label}</td>
                                            <td className={`px-3 py-2 text-right tabular-nums font-semibold ${row.monthlyNet >= 0 ? "text-green-600" : "text-red-600"}`}>
                                                {formatCompact(row.monthlyNet)}
                                            </td>
                                            <td className="px-3 py-2 text-right tabular-nums">{formatMonths(row.runwayMonths)}</td>
                                            <td className="px-3 py-2">
                                                <span className={`inline-flex items-center rounded px-2 py-0.5 text-[10px] font-medium ${
                                                    row.assessment === "healthy"
                                                        ? "bg-green-100 text-green-700"
                                                        : row.assessment === "watch"
                                                          ? "bg-amber-100 text-amber-700"
                                                          : "bg-red-100 text-red-700"
                                                }`}>
                                                    {row.assessment}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </section>

                    <section className="rounded-2xl border border-[#E2E8F0] bg-white p-5 shadow-sm">
                        <h3 className="mb-3 text-sm font-bold uppercase tracking-[2px] text-[#209DB3]">Business Context Signals</h3>
                        {topContext.length === 0 ? (
                            <p className="text-xs text-muted-foreground">
                                No approved business context items yet. Add and approve context in Knowledge/Insights to improve CEO commentary.
                            </p>
                        ) : (
                            <div className="space-y-2">
                                {topContext.map((item) => (
                                    <div key={item.id} className="rounded-lg border p-3">
                                        <div className="flex items-start gap-2">
                                            <FileTextIcon className="mt-0.5 size-3.5 text-[#209DB3]" />
                                            <div className="min-w-0">
                                                <p className="text-xs font-semibold text-[#0F172A]">{item.title}</p>
                                                <p className="text-xs text-muted-foreground mt-0.5">{item.summary}</p>
                                                <p className="text-[10px] text-muted-foreground mt-1">
                                                    Impact: {item.impact_hint} {item.source_document_filename ? `| Source: ${item.source_document_filename}` : ""}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </section>
                </div>

                <section className="space-y-3">
                    {generatedInsights.map((note, idx) => (
                        <div
                            key={`${note.type}-${idx}`}
                            className={`rounded-r-2xl border-l-4 p-4 text-sm ${
                                note.type === "insight"
                                    ? "border-l-[#209DB3] bg-[#F0FDFA] text-[#0F172A]"
                                    : "border-l-[#FF5100] bg-[#FFF7ED] text-[#7C2D12]"
                            }`}
                        >
                            <div className="flex items-center gap-2">
                                {note.type === "insight" ? (
                                    <TrendingUpIcon className="size-4 text-[#209DB3]" />
                                ) : (
                                    <AlertTriangleIcon className="size-4 text-[#FF5100]" />
                                )}
                                <span>{note.text}</span>
                            </div>
                        </div>
                    ))}

                    {avgNet < 0 && (
                        <div className="rounded-r-2xl border-l-4 border-l-[#DC2626] bg-[#FEF2F2] p-4 text-sm text-[#7F1D1D]">
                            <div className="flex items-center gap-2">
                                <TrendingDownIcon className="size-4 text-[#DC2626]" />
                                <span>Current cash trend is negative. Review forecast assumptions and short-term payment timing.</span>
                            </div>
                        </div>
                    )}
                </section>

                <ReconciliationChecks checks={forecastData?.checks ?? []} />
            </div>
        </div>
    );
}

function ReconciliationChecks({checks}: {checks: ThreeStatementCheck[]}) {
    const [expanded, setExpanded] = useState(false);
    const cfChecks = checks.filter((c) => c.statement === "cf" || c.key?.startsWith("cf"));
    if (cfChecks.length === 0) return null;

    return (
        <section className="rounded-2xl border border-[#E2E8F0] bg-white p-5 shadow-sm">
            <button
                type="button"
                className="flex w-full items-center justify-between gap-3 text-left"
                onClick={() => setExpanded((value) => !value)}
                aria-expanded={expanded}
                aria-controls="cf-engine-checks-panel"
            >
                <div>
                    <h3 className="text-sm font-bold uppercase tracking-[2px] text-[#209DB3]">Engine Checks</h3>
                    <p className="mt-1 text-xs text-muted-foreground">Hidden by default on summary tabs. Expand to inspect model reconciliation details.</p>
                </div>
                <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">{expanded ? "Hide" : "Show"} {cfChecks.length}</span>
                    <ChevronDownIcon className={`size-4 text-muted-foreground transition-transform ${expanded ? "rotate-180" : ""}`} />
                </div>
            </button>

            {expanded && (
                <div id="cf-engine-checks-panel" className="mt-3 space-y-2">
                    {cfChecks.map((check, idx) => (
                        <div key={`${check.key}-${idx}`} className={`flex items-start gap-2 rounded-lg border p-3 text-xs ${
                            check.status === "PASS" ? "border-green-200 bg-green-50/50" :
                            check.status === "FAIL" ? "border-red-200 bg-red-50/50" :
                            "border-amber-200 bg-amber-50/50"
                        }`}>
                            {check.status === "PASS"
                                ? <CheckCircle2Icon className="mt-0.5 size-3.5 shrink-0 text-green-600" />
                                : <XCircleIcon className="mt-0.5 size-3.5 shrink-0 text-red-600" />}
                            <div className="min-w-0">
                                <span className="font-semibold">{check.label}</span>
                                {check.month_key && <span className="ml-2 text-muted-foreground">{check.month_key}</span>}
                                {check.details && <p className="mt-0.5 text-muted-foreground">{check.details}</p>}
                                {check.delta != null && check.status === "FAIL" && (
                                    <p className="mt-0.5 text-red-600">Delta: ${Math.abs(check.delta).toLocaleString(undefined, {maximumFractionDigits: 2})}</p>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </section>
    );
}

function MetricCard({
    label,
    value,
    emphasis = "neutral",
}: {
    label: string;
    value: string;
    emphasis?: "neutral" | "good" | "bad";
}) {
    return (
        <div className="rounded-2xl border border-[#E2E8F0] bg-white p-4 text-center shadow-sm">
            <p
                className={`text-2xl font-extrabold tabular-nums ${
                    emphasis === "good"
                        ? "text-green-600"
                        : emphasis === "bad"
                          ? "text-red-600"
                          : "text-[#209DB3]"
                }`}
            >
                {value}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">{label}</p>
        </div>
    );
}
